import { useCallback, useContext, useMemo } from "react";

import { IIrctcNonPnrHalfcardData, IIrctcPayload } from "@swiggy-private/cart-types";

import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { PnrDataContext } from "@cart-client/contexts/PnrDataContext";
import { getNonPnrIrctcData } from "@cart-client/helpers/Irctc";

export interface UsePnrDataReturn {
    nonPnrHalfcardData?: IIrctcNonPnrHalfcardData;
    updatePnrDetails: (pnr: string) => Promise<unknown> | undefined;
    handleClosePnrHalfCard: () => void;
}

export const usePnrData = (): UsePnrDataReturn => {
    const { setIsEnterPnr, setIsIrctcPnrHalfCardOpen } = useContext(PnrDataContext);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState;

    const nonPnrHalfcardData = useMemo(
        () => getNonPnrIrctcData(cartResponse) as IIrctcNonPnrHalfcardData | undefined,
        [cartResponse],
    );

    const updatePnrDetails = useCallback(
        (pnr: string) => {
            const updatePromise = doUpdateCart({
                ...localCart,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                irctc_data: { ...localCart?.irctc_data, pnr_number: pnr } as IIrctcPayload,
            });
            updatePromise?.finally(() => {
                setIsIrctcPnrHalfCardOpen(false);
                setIsEnterPnr(false);
            });
            return updatePromise;
        },
        [doUpdateCart, localCart, setIsIrctcPnrHalfCardOpen, setIsEnterPnr],
    );

    const handleClosePnrHalfCard = useCallback(() => {
        setIsIrctcPnrHalfCardOpen(false);
        setIsEnterPnr(false);
    }, [setIsIrctcPnrHalfCardOpen, setIsEnterPnr]);

    return {
        nonPnrHalfcardData,
        updatePnrDetails,
        handleClosePnrHalfCard,
    };
};
